import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '@mui/material/Modal';
import { Box, Tabs, Tab } from '@mui/material';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import CommissioningSignOff from 'src/screen/Protected/DeviceScreen/Commissioning/CommissioningSignOff';
import Button from 'src/component/UI/Button';
import { getDeviceById } from 'src/module/device/selector';
import { Map, fromJS } from 'immutable';
import { getAbilities } from 'src/module/authentication/selector';
import { fetchCheckSheet, saveCheckSheet, updateCheckSheet, fetchCheckSheetTasks } from 'src/module/checkSheet/action';
import { getCheckSheetReport, getCheckSheetTasks, getCheckSheetTasksLoading, getSaveCheckSheetReportLoading } from 'src/module/checkSheet/selector';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import ControllerInfo from './ControllerInfo';

const selectExternalTasks = getCheckSheetTasks('commissioning-controller-external');
const selectInternalTasks = getCheckSheetTasks('commissioning-controller-internal');
const selectProgrammingTasks = getCheckSheetTasks('commissioning-controller-programming');

export default function CommissioningControllerReport (props) {
  const { deviceId, reportId, open } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState('1');
  const externalTasks = useSelector(selectExternalTasks);
  const internalTasks = useSelector(selectInternalTasks);
  const programmingTasks = useSelector(selectProgrammingTasks);
  const report = useSelector(getCheckSheetReport(reportId));
  const device = useSelector(getDeviceById(deviceId));
  const isSaveLoading = useSelector(getSaveCheckSheetReportLoading('commissioning-controller'));
  const isLoadingTasks = useSelector(getCheckSheetTasksLoading);
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const abilities: any = useSelector(getAbilities);
  const isPhoneView = useMediaQuery(`(max-width: ${variables.phoneWidth})`);

  const canEdit = abilities.includes('edit-check-sheet-report');
  const readOnly = Boolean(reportId) && !canEdit;

  const buildEmptyResults = (external, internal, programming) => {
    const toTaskList = (tasks) => {
      const list = tasks.toJS ? tasks.toJS() : [];
      return list.map((task: any) => ({
        task_id: task.key,
        status: null,
        comment: '',
      }));
    };
    return fromJS({
      info: {
        type: 'commissioning-controller',
        deviceId: deviceId || null,
      },
      'commissioning-controller-external': toTaskList(external),
      'commissioning-controller-internal': toTaskList(internal),
      'commissioning-controller-programming': toTaskList(programming),
      extras: {
        controllerInfo: {
          projectNo: '', area: '', controllerNo: device?.get('tag') || '', distributionPanel: '',
        },
      },
      signOff: {
        recommendations: '',
        commissioningStatus: '',
      },
    }) as Map<string, any>;
  };

  const [allResults, setAllResults] = useState(Map({}));
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    dispatch(fetchCheckSheetTasks('commissioning-controller-external'));
    dispatch(fetchCheckSheetTasks('commissioning-controller-internal'));
    dispatch(fetchCheckSheetTasks('commissioning-controller-programming'));
  }, [dispatch]);

  useEffect(() => {
    if (!reportId && externalTasks?.size > 0 && internalTasks?.size > 0 && programmingTasks?.size > 0) {
      setAllResults(buildEmptyResults(externalTasks, internalTasks, programmingTasks));
    }
  }, [externalTasks, internalTasks, programmingTasks, reportId]);

  useEffect(() => {
    if (open && !reportId) {
      setTabValue('1');
      setSubmitAttempted(false);
      if (externalTasks?.size > 0 && internalTasks?.size > 0 && programmingTasks?.size > 0) {
        setAllResults(buildEmptyResults(externalTasks, internalTasks, programmingTasks));
      }
    }
  }, [open]);

  useEffect(() => {
    dispatch(fetchCheckSheetTasks('commissioning-controller-external'));
    dispatch(fetchCheckSheetTasks('commissioning-controller-internal'));
    dispatch(fetchCheckSheetTasks('commissioning-controller-programming'));
  }, [dispatch]);

  useEffect(() => {
    if (open && reportId) {
      dispatch(fetchCheckSheet(reportId, 'commissioning-controller-external'));
    }
  }, [open, reportId, dispatch]);

  useEffect(() => {
    if (reportId && report.get('checkSheet')) {
      setAllResults(Map(report));
    }
  }, [report, reportId]);

  if (!open) {
    return null;
  }

  if (reportId && !report.get('checkSheet')) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const statusError = submitAttempted && !allResults.getIn(['signOff', 'commissioningStatus']);

  const next = () => setTabValue((parseInt(tabValue) + 1).toString());
  const previous = () => setTabValue((parseInt(tabValue) - 1).toString());

  const submit = () => {
    if (!allResults.getIn(['signOff', 'commissioningStatus'])) {
      setSubmitAttempted(true);
      setTabValue('5');
      return;
    }
    const values: any = allResults.toJS();
    const external = [...(values['commissioning-controller-external'] || [])];
    const internal = [...(values['commissioning-controller-internal'] || [])];
    const programming = [...(values['commissioning-controller-programming'] || [])];
    const checkSheet = [...external, ...internal, ...programming];
    let payload = allResults
      .setIn(['info', 'deviceId'], deviceId || null)
      .set('checkSheet', fromJS(checkSheet))
      .set('signOff', allResults.get('signOff'));
    if (reportId) {
      payload = payload.set('id', reportId);
      dispatch(updateCheckSheet(payload, deviceId, 'commissioning-controller'));
    } else {
      dispatch(saveCheckSheet('commissioning-controller', payload, deviceId));
    }
  };

  const change = (path, value) => {
    setAllResults(prev => prev.setIn(path, value));
  };

  const titleTag = device ? device.get('tag') : null;

  return (
    <Modal open={props.open} onClose={props.handleClose} style={{ height: '90vh' }}>
      <div className={`modal testing-report-modal commissioning-modal${isPhoneView ? ' modal-no-padding-sides' : ''}`}>
        {!isMobile && (
          <h2>{t('commissioningController.modalTitle')} {titleTag && <b>({titleTag})</b>}</h2>
        )}
        {isLoadingTasks ? (
          <div style={{ margin: 'auto', marginTop: '200px', width: '100px' }}>
            <LogoSpinner size={100} />
          </div>
        ) : (
          <>
            <Box sx={{ width: '100%', marginBottom: '20px' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                textColor='primary'
                indicatorColor='primary'
                aria-label="commissioning-controller-tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="1" label={<span className='tab-label-size'>{t('commissioningController.info')}</span>} />
                <Tab value="2" label={<span className='tab-label-size'>{t('commissioningController.externalInspection')}</span>} />
                <Tab value="3" label={<span className='tab-label-size'>{t('commissioningController.internalInspection')}</span>} />
                <Tab value="4" label={<span className='tab-label-size'>{t('commissioningController.programmingInspection')}</span>} />
                <Tab value="5" label={<span className='tab-label-size'>{t('commissioningController.signOffAndComments')}</span>} />
              </Tabs>
            </Box>

            {tabValue === '1' && (
              <ControllerInfo
                readOnly={readOnly}
                data={allResults?.get('extras')}
                onChange={change}
              />
            )}
            {tabValue === '2' && (
              <CheckSheet
                readOnly={readOnly}
                onChange={change}
                id='commissioning-controller-external'
                title={t('commissioningController.externalInspection')}
                data={allResults?.get('commissioning-controller-external')}
                checkSheetType="commissioning-controller-external"
              />
            )}
            {tabValue === '3' && (
              <CheckSheet
                readOnly={readOnly}
                onChange={change}
                id='commissioning-controller-internal'
                title={t('commissioningController.internalInspection')}
                data={allResults?.get('commissioning-controller-internal')}
                checkSheetType="commissioning-controller-internal"
              />
            )}
            {tabValue === '4' && (
              <CheckSheet
                readOnly={readOnly}
                onChange={change}
                id='commissioning-controller-programming'
                title={t('commissioningController.programmingInspection')}
                data={allResults?.get('commissioning-controller-programming')}
                checkSheetType="commissioning-controller-programming"
              />
            )}
            {tabValue === '5' && (
              <CommissioningSignOff
                readOnly={readOnly}
                data={allResults?.get('signOff')}
                onChange={change}
                statusError={statusError}
              />
            )}

            <div className='button-bar stay-at-bottom'>
              <Button onClick={() => props.handleClose()}>{t('common.cancel')}</Button>
              {tabValue !== '1' && <Button onClick={previous}>{t('common.previous')}</Button>}
              {tabValue === '5'
                ? <Button onClick={submit} disabled={readOnly} loading={isSaveLoading} cta>{reportId ? t('common.save') : t('common.submit')}</Button>
                : <Button className='next-button' onClick={next} cta>{t('common.next')}</Button>
              }
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

CommissioningControllerReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
